import { z } from 'zod';
const calendarDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(value => {
    const date = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}, 'Expected a real calendar date');
const text = z.string().trim().min(1).max(200);
const nullableText = z.string().nullable();
const noticeFields = {
    employeeId: text,
    noticeId: text,
    revision: text,
    documentUrl: z.string().url().max(2048).refine(value => new URL(value).protocol === 'https:', 'Expected HTTPS'),
    issuedOn: calendarDate,
    issuedFinal: z.literal(true),
    serviceEvidence: z.string().trim().min(1).max(1000),
};
/** Operator attestation for this preview only, not a provider event contract. */
export const allocationHrPreviewRequestSchema = z.object({
    nod: z.object({ ...noticeFields, incidentId: z.string().uuid(), outcome: text }).strict().optional(),
    terminationNotice: z.object({
        ...noticeFields, effectiveDate: calendarDate, nodNoticeId: text, nodRevision: text,
        noteContext: z.object({
            receivedOn: calendarDate,
            reason: z.string().trim().min(1).max(10000),
            nodEntry: z.string().trim().min(1).max(10000),
        }).strict().optional(),
        /**
         * The Employee Login Access Deactivation form's own fields, when one was
         * raised. `selectedReason` is the form's radio selection
         * (`inf_option_Reasonforimmediatedeactivation`) — an option id or its
         * label — and it is the only thing that classifies the outcome. The form
         * also reports preventive suspension, floating status and two failed-training
         * outcomes, so supplying this constrains the proposal rather than supporting
         * it. `notes` is `inf_custom_BadgeFour`, carried as evidence and never
         * parsed. `receivedTimezone` is required to turn `receivedAt` into a date;
         * without it no receipt date is derived, because the clock the source keeps
         * its calendar on is not established.
         */
        accessDeactivation: z.object({
            selectedReason: text,
            notes: z.string().trim().max(2000).optional(),
            lastDayOfWork: calendarDate,
            receivedOn: calendarDate.optional(),
            receivedAt: z.string().datetime({ offset: true }).optional(),
            receivedTimezone: z.string().trim().min(1).max(64).optional(),
        }).strict().optional(),
    }).strict().optional(),
}).strict();
export const allocationHrDoerEvidenceSchema = z.object({
    id: z.string().uuid(), employee_id: nullableText, doer_code: z.string(),
    first_name: z.string(), last_name: z.string(), company_email: z.string(), personal_email: nullableText,
    doer_type: nullableText, category: nullableText, section: nullableText, matching_status: nullableText,
    employee_status: nullableText, availability_status: nullableText, for_match_flag: nullableText,
    founder_id_ref: nullableText, founder_id_ref_2: nullableText, founder_name: nullableText,
    pt_founder_2: nullableText, end_date: nullableText, separation_date: nullableText,
    quick_notes: nullableText, updated_at: z.string(),
}).strict();
export const allocationHrIncidentEvidenceSchema = z.object({
    id: z.string().uuid(), doer_id: z.string().uuid().nullable(), doer_code: nullableText,
    doer_email: nullableText, title: nullableText, status: nullableText,
    incident_date: nullableText, reported_date: nullableText, resolved_date: nullableText,
    resolution: nullableText, notes: nullableText, updated_at: z.string(),
}).strict();
export const allocationHrAssignmentEvidenceSchema = z.object({
    id: z.string().uuid(), doer_id: z.string().uuid(), founder_id: z.string().uuid().nullable(),
    founder_code: nullableText, status: z.string(), assignment_type: nullableText,
    start_date: nullableText, end_date: nullableText,
}).strict();
const findingSchema = z.object({
    code: z.string(), severity: z.enum(['missing', 'conflict', 'review', 'info']),
    scope: z.enum(['context', 'nod', 'termination']), incidentId: z.string().uuid().nullable(), detail: z.string(),
}).strict();
export const allocationHrPreviewResponseSchema = z.object({
    contractVersion: z.literal('1'), readOnly: z.literal(true), automationEnabled: z.literal(false),
    generatedAt: z.string().datetime(), evidenceVerification: z.enum(['not_provided', 'operator_attested_not_verified']),
    suppliedBy: z.string().nullable(), asOfDate: calendarDate,
    doer: allocationHrDoerEvidenceSchema,
    incidents: z.array(allocationHrIncidentEvidenceSchema),
    assignments: z.array(allocationHrAssignmentEvidenceSchema),
    completeness: z.object({
        incidents: z.boolean(), assignments: z.boolean(), employeeIdentityMatches: z.number().int().nonnegative(),
        /**
         * Whether Allocation currently offers the terminal board values as active
         * dropdown options. `null` means the options could not be read, which is
         * not the same as their being absent.
         */
        terminalBoardOptions: z.enum(['available', 'unavailable']).nullable(),
    }).strict(),
    findings: z.array(findingSchema),
    nodProposal: z.object({
        evidenceOrigin: z.literal('operator_attested'), noticeId: z.string(), revision: z.string(), note: z.string(),
    }).strict().nullable(),
    terminationProposal: z.object({
        evidenceOrigin: z.literal('operator_attested'), noticeId: z.string(), revision: z.string(),
        effectiveDate: calendarDate, reviewRequired: z.literal(true),
        changes: z.object({ category: z.literal('Archive'), section: z.literal('Archived'), matching_status: z.literal('Terminated') }).strict(),
    }).strict().nullable(),
    terminationNoteProposal: z.object({
        evidenceOrigin: z.literal('operator_attested'), noticeId: z.string(), revision: z.string(),
        receivedOn: calendarDate, note: z.string(), proposedQuickNotes: z.string(), changed: z.boolean(),
    }).strict().nullable(),
}).strict();
//# sourceMappingURL=allocation-hr-preview.js.map